import { IMAGE_CONFIG } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { IconService } from './layout/service/icon.service';
import { ChunkErrorHandlerService } from './core/services/chunk-error-handler.service';
import { AuthService } from './core/services/auth.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
  withPreloading,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { ViewerAwarePreloadingStrategy } from './core/strategies/viewer-aware-preloading.strategy';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MessageService } from '@progress/kendo-angular-l10n';
import { KendoGridMessagesService } from './shared/services/kendo-grid-messages.service';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { FormioAppConfig } from '@formio/angular';
import { environment } from '../environments/environment';
import { waitForReduxDevtoolsExtension } from './core/utils/redux-devtools-wait.util';


function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.microsoftClientId,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    system: {
      allowPlatformBroker: false,
      loggerOptions: { logLevel: LogLevel.Info, piiLoggingEnabled: false },
    },
  });
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: ['user.read'] },
    loginFailedRoute: '/login',
  };
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withHashLocation(),
      withPreloading(ViewerAwarePreloadingStrategy),
    ),
    provideHttpClient(withInterceptors([authInterceptor]), withInterceptorsFromDi()),
    provideAppInitializer(() => inject(IconService).preload()),
    provideAppInitializer(() => inject(ChunkErrorHandlerService).init()),
    // Ensures a restored session's api/Role/GetByUserId permissions are already in the store
    provideAppInitializer(() => inject(AuthService).waitUntilPermissionsReady()),
    // Dev-only: avoid the Redux DevTools extension-injection race (see waitForReduxDevtoolsExtension)
    provideAppInitializer(() => waitForReduxDevtoolsExtension({ isProduction: environment.production })),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimations(),
    { provide: IMAGE_CONFIG, useValue: { disableImageSizeWarning: true } },
    provideTranslateService({ lang: 'en' }),
    provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    // Localizes Kendo Grid's built-in pager text (see kendo-grid-messages.service.ts) app-wide.
    { provide: MessageService, useClass: KendoGridMessagesService },
    // ── Microsoft MSAL ──────────────────────────────────────────────────────
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: FormioAppConfig, useValue: new FormioAppConfig({ apiUrl: environment.endPoint, appUrl: environment.endPoint }) },
  ],
};
